<template>
  <CTableHead color="light">
    <CTableRow>
      <CTableHeaderCell
        scope="col"
        v-for="(column, index) in columns"
        :key="index"
        :class="[
          { ['column-' + index]: true },
          { ['with-sorter']: withSorter },
          { 'is-resizable': resizable },
        ]"
        :style="headerStyle(index)"
        @click="(e) => onClickHeader(e, index)"
      >
        <div class="header-cell">
          <div class="text">{{ column }}</div>
          <div class="icon" v-if="withSorter && !exclude.includes(index)">
            <CIcon :icon="toggleIcon" size="sm" :key="toggler[index]" />
          </div>
        </div>
        <span
          v-if="resizable"
          class="col-resize-handle"
          title="Drag to resize"
          @mousedown.prevent.stop="onResizeStart($event, index)"
        />
      </CTableHeaderCell>
    </CTableRow>
  </CTableHead>
</template>

<script>
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'HeaderRow',

  props: {
    withSorter: {
      type: Boolean,
      default: false,
    },
    columns: {
      required: true,
    },
    resizable: {
      type: Boolean,
      default: false,
    },
    columnWidths: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      exclude: ['action', 'status', 'extra', 'moreInfo'],
    }
  },

  setup(props, { emit }) {
    const MIN_WIDTH = 64
    const toggler = ref([])
    const order = ref('ASC')

    const headerStyle = (key) => {
      const width = Number(props.columnWidths?.[key])
      if (!width) {
        return {}
      }
      return { '--table-col-width': `${width}px` }
    }

    const onClickHeader = (e, index) => {
      if (!props.withSorter) {
        e.stopPropagation()
        return
      }

      toggler.value[index] = !toggler.value[index]
      order.value = toggler.value[index] ? 'DESC' : 'ASC'

      emit('sort:column', { index, order: order.value })
    }

    const onResizeStart = (event, key) => {
      const handle = event.currentTarget
      const header = handle?.parentElement
      const startX = event.clientX
      const startWidth =
        Number(props.columnWidths?.[key]) ||
        header?.getBoundingClientRect().width ||
        MIN_WIDTH

      const onMove = (moveEvent) => {
        const width = Math.max(
          MIN_WIDTH,
          Math.round(startWidth + (moveEvent.clientX - startX)),
        )
        emit('resize:column', { key, width })
      }

      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.removeProperty('cursor')
        document.body.style.removeProperty('user-select')
        emit('resize:end')
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }

    const toggleIcon = computed(() => {
      return order.value == 'DESC' ? 'cil-chevron-bottom' : 'cil-chevron-top'
    })

    onMounted(() => {
      Object.entries(props.columns).forEach((col) => {
        toggler.value[col[0]] = false
      })
    })

    return { toggler, onClickHeader, onResizeStart, headerStyle, toggleIcon }
  },

  emits: ['sort:column', 'resize:column', 'resize:end'],
}
</script>

<style lang="scss" scoped>
.is-resizable {
  position: relative;
}

.header-cell .text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-resize-handle {
  position: absolute;
  top: 0;
  right: -4px;
  z-index: 2;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;

  &:hover,
  &:active {
    background: rgba(50, 31, 219, 0.18);
  }
}
</style>
